/*
Copyright (c) 2024, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/

import React, { memo, useCallback, useEffect, useState } from 'react';
import { Accordion as AccordionV, AccordionItemInterface } from '@veneer/core';
import './styles.scss';
import { withAnalytics } from '@veneer/analytics';
import {
  AccordionBodyProps,
  AccordionHeaderProps,
  AccordionItemsProps,
  AccordionProps
} from './types';

/**
 * UI Toolkit component based on Veneer Accordion
 * @public
 */
const TAccordion: React.FC<AccordionProps> = ({
  dividerBottom,
  dividerTop,
  ...props
}) => {
  const [data, setData] = useState<AccordionItemInterface[]>([]);

  const handleExpand = (
    event: any,
    index: number,
    item: AccordionItemsProps
  ) => {
    const updatedData = data ? [...data] : [];
    if (updatedData && updatedData.length > 0)
      updatedData[index].expanded = true;
    setData(updatedData);
    props.onExpand && props.onExpand(event, index, item);
  };

  const handleCollapse = (
    event: any,
    index: number,
    item: AccordionItemsProps
  ) => {
    const updatedData = data ? [...data] : [];
    if (updatedData && updatedData.length > 0)
      updatedData[index].expanded = false;
    setData(updatedData);
    props.onCollapse && props.onCollapse(event, index, item);
  };

  useEffect(() => {
    const items: AccordionItemInterface[] = React.Children.map(
      props.children,
      (child, index) => {
        const itemProps = (child as React.ReactElement<AccordionItemsProps>)
          .props;
        const isExpanded = itemProps.expanded;
        const header = itemProps
          .children[0] as React.ReactElement<AccordionHeaderProps>;

        const content = itemProps
          .children[1] as React.ReactElement<AccordionBodyProps>;

        return {
          disabled: itemProps.disabled,
          content,
          expanded: isExpanded,
          header: {
            startArea: header
          },
          id: itemProps.disabled
            ? `accordion-item-disabled-${index}`
            : `accordion-item-${index}`
        };
      }
    );
    setData(items);
  }, [props.children, props.size]);

  const handleAccordionClass = useCallback(() => {
    let baseCls = 'clientos-ui-toolkit-accordion';
    baseCls += ` ${props.size ?? 'xs'}`;
    return baseCls;
  }, [props.size]);

  return (
    <div
      className={handleAccordionClass()}
      id={props?.id}
    >
      {dividerTop && <div className="clientos-ui-toolkit-accordion__divider" />}
      <AccordionV
        items={data}
        onCollapse={handleCollapse}
        onExpand={handleExpand}
        appearance="outlined"
        behavior={props.behavior}
      />
      {dividerBottom && (
        <div className="clientos-ui-toolkit-accordion__divider" />
      )}
    </div>
  );
};

export const AccordionAnalytics = withAnalytics(TAccordion);
export default memo(TAccordion);
